import { PopupProps, StandardProps } from '@/kind';

export enum WIDGET_KIND {
  POPUP = 'popup',
  STANDARD = 'standard',
}

export const enum WIDGET_TYPE {
  SEARCH = 'Search',
  HELP = 'Help',
  AGENT_ASSIST = 'Agent Assist',
}

export const enum WIDGET_SOURCE {
  WEB = 'web',
  PLATFORM = 'platform',
  AGENT_ASSIST = 'agent_assist',
  TEST_INTENT = 'test_intent',
}

// WIDGET_NAMES constant object where key is WIDGET_KIND and value is the widget name
export const WIDGET_NAMES: {
  [key in WIDGET_KIND]: string;
} = {
  [WIDGET_KIND.POPUP]: 'brainfish-popup-help-widget',
  [WIDGET_KIND.STANDARD]: 'brainfish-search-widget',
};

// use process.env.VERSION to get the version from the package.json
export const VERSION = import.meta.env.VITE_VERSION as string;

// NOTE: introducing new props requires adding a default here
export const defaultSearchProps: StandardProps = {
  version: VERSION,
  disableTracking: false,
  widgetSource: WIDGET_SOURCE.WEB,
  widgetType: WIDGET_TYPE.SEARCH,
  isChromeExtension: false,
  accessToken: '',
  apiKey: '',
  apiHost: '',
  language: 'en',
  theme: {},
  class: '',
  settings: {},
  input: undefined,
  onToggle: () => {},
  autoFocus: false,
  disableModalBackdrop: false,
  searchIntentId: '',
};

export const defaultPopupProps: PopupProps = {
  ...defaultSearchProps,
  onClose: undefined,
  onOpen: undefined,
  theme: {},
  autoShowDelay: undefined,
  isOpen: undefined,
  defaultOpen: undefined,
  widgetMode: undefined,
  autoFocus: true,
  disableModalBackdrop: false,
};

type UrlString = string & { __url: never };

const isUrlString = (value: string): value is UrlString => {
  const urlRegExp = /^(https?|ftp):\/\/[^\s/$.?#].[^\s]*$/i;

  return urlRegExp.test(value);
};

export const validateUrlString = (value: string): UrlString => {
  if (!isUrlString(value)) {
    throw new Error(`Invalid URL: ${value}`);
  }

  return value as UrlString;
};

export type NextBestAction = {
  label: string;
  type: string;
  icon: string | UrlString;
  value: any;
};

export type Action = {
  label: string;
  type: string;
  icon: string | UrlString;
  value: any;
};

export const enum Feedback {
  REJECT = 'reject',
  ACCEPT = 'accept',
}

export const enum EventType {
  InputFieldSearch = 'SEARCH_WIDGET_SEARCH_ARTICLE',
  NoArticlesFound = 'SEARCH_WIDGET_NO_ARTICLES_FOUND',
  FirstStreamedAnswerChunkReceived = 'SEARCH_WIDGET_FIRST_STREAMED_ANSWER_CHUNK_RECEIVED',
  AnswerNotFound = 'SEARCH_WIDGET_ANSWER_NOT_FOUND',
  AnswerFound = 'SEARCH_WIDGET_ANSWER_FOUND',
  AnswerAccepted = 'SEARCH_WIDGET_ANSWER_ACCEPTED',
  AnswerRejected = 'SEARCH_WIDGET_ANSWER_REJECTED',
  RecentSearchClicked = 'SEARCH_WIDGET_RECENT_SEARCH_CLICKED',
  TypedQuestionSumitted = 'SEARCH_WIDGET_SUBMIT_SEARCH',
  SearchResultClicked = 'SEARCH_WIDGET_SEARCH_RESULT_CLICKED',
  SearchCategoryClicked = 'SEARCH_WIDGET_SEARCH_CATEGORY_CLICKED',
  SearchWidgetAction = 'SEARCH_WIDGET_ACTION',
  OpenWidget = 'SEARCH_WIDGET_OPEN_WIDGET',
  CloseWidget = 'SEARCH_WIDGET_CLOSE_WIDGET',
  WidgetLoaded = 'WIDGET_LOADED',
  IntentIdentified = 'SEARCH_WIDGET_INTENT_IDENTIFIED',
  IntentActionClicked = 'SEARCH_WIDGET_INTENT_ACTION_CLICKED',
  AskNewQuestion = 'SEARCH_WIDGET_ASK_NEW_QUESTION_CLICKED',
  FeedbackReasonSubmitted = 'SEARCH_WIDGET_FEEDBACK_REASON_SUBMITTED',
}

export const enum WidgetEvents {
  OnClosed = 'onBrainfishHelpWidgetClosed',
  OnOpened = 'onBrainfishHelpWidgetOpened',
}

type SearchIntentFilters = {
  collection?: any;
  channel?: any;
  region?: any;
};

export type SearchIntentAction = {
  id: string;
  type: string;
  action: {
    options: {
      title: string;
    };
  };
};

export type SearchIntent = {
  id: string;
  name: string;
  filters?: SearchIntentFilters;
  actions: SearchIntentAction[];
};

export type PlatformIdentifyResponse =
  | {
      searchIntentIdentified: false;
      message: string;
    }
  | {
      searchIntentIdentified: true;
      searchIntent: SearchIntent;
    };
